'use client'

import { useFormContext } from 'react-hook-form'
import { noop } from 'lodash'

import { Text } from '@vinted/web-ui'

import { SelectableInputDropdown } from '@marketplace-web/common-components/selectable-input-dropdown-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import { Field } from '../../../constants'
import { FormModel } from '../../../types'

type Props = {
  name: Field
  testId?: string
  onSave?: () => void
} & Pick<
  React.ComponentProps<typeof SelectableInputDropdown>,
  'inputTitle' | 'placeholder' | 'required'
> & {
    data: Array<{
      id: string
      title: string
    }>
  }

const AdvertisingSelect = ({
  name,
  onSave = noop,
  data,
  required,
  inputTitle,
  placeholder,
  testId,
}: Props) => {
  const translate = useTranslate('advertising.form.validation')
  const selectableInputDropdownTranslate = useTranslate()
  const { register, watch, setValue, formState } = useFormContext<FormModel>()
  const breakpoints = useBreakpoint()

  const selectedId = watch(name)

  const { name: registeredName } = register(name, { required })

  return (
    <SelectableInputDropdown
      isPhone={!!breakpoints.phones}
      name={registeredName}
      data={data}
      inputTitle={inputTitle}
      textSave={selectableInputDropdownTranslate('actions.save')}
      placeholder={placeholder}
      selectedValue={data.find(({ id }) => id === selectedId)}
      closeOnDropdownBlur
      testId={testId}
      onSave={value => {
        setValue(name, value ? String(value.id) : undefined, { shouldValidate: true })
        onSave()
      }}
      aria={{
        'aria-invalid': !!formState.errors[name],
        'aria-errormessage': `${name}-error`,
        'aria-required': required,
      }}
      validation={
        formState.errors[name] && (
          <Text type={Text.Type.Caption} theme="warning" as="p" id={`${name}-error`}>
            {translate('required')}
          </Text>
        )
      }
    />
  )
}

export default AdvertisingSelect
